import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import ChartContainer from '../charts/ChartContainer';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

const CallVolumePerHour = ({ data, filters }) => {

  const getDateStr = (date) =>
    date instanceof Date ? date.toLocaleDateString('en-CA') : new Date(date).toLocaleDateString('en-CA');

  // Filter data based on filters
  const filtered = (filters.selectedDate ? data.filter(d => getDateStr(d.Date) === filters.selectedDate) : data).filter((d) => {
    const matchCustomer = !filters.customer || d.Customer === filters.customer;
    const matchAgent = !filters.agent || d.Agent === filters.agent;
    return matchCustomer && matchAgent;
  });

  // Totals to show in header
  let totalHandled = 0;
  let abandonedNoInteractionTotal = 0;
  let abandonedDuringRingTotal = 0;
  let abandonedAfterInteractionTotal = 0;

  // Build segments map for classification of abandoned calls
  const segmentsForTotals = {};

  filtered.forEach((d) => {
    const segmentId = d.Segment;

    if (!segmentsForTotals[segmentId]) {
      segmentsForTotals[segmentId] = {
        hadMissedOrRejected: false,
        hadRing: false,
        hadAbandoned: false,
        firstDate: d.Date instanceof Date ? d.Date : new Date(d.Date),
      };
    }

    const validMissed = d.Missed === 1 && d.RingTime >= 20;

    if (d.Rejected === 1 || validMissed) {
      segmentsForTotals[segmentId].hadMissedOrRejected = true;
    }
    if (d.RingTime > 0) {
      segmentsForTotals[segmentId].hadRing = true;
    }
    if (d.Abandoned === 1) {
      segmentsForTotals[segmentId].hadAbandoned = true;
    }

    totalHandled += d.Handled;
  });

  // Count totals for abandoned classification from segments
  const seenSegments = new Set();
  Object.entries(segmentsForTotals).forEach(([segmentId, seg]) => {
    if (!seg.hadAbandoned || seenSegments.has(segmentId)) return;
    if (seg.hadMissedOrRejected) abandonedAfterInteractionTotal += 1;
    else if (seg.hadRing) abandonedDuringRingTotal += 1;
    else abandonedNoInteractionTotal += 1;
    seenSegments.add(segmentId);
  });

  // Unique dates count for header when no specific date is selected
  const uniqueDatesSet = new Set(
    (filters.selectedDate ? data.filter(d => getDateStr(d.Date) === filters.selectedDate) : data)
      .map(d => getDateStr(d.Date))
  );
  const uniqueDatesCount = uniqueDatesSet.size;

  // Prepare data grouped by hour for the bar chart
  const grouped = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    Handled: 0,
    Abandoned_NoInteraction: 0,
    Abandoned_DuringRing: 0,
    Abandoned_AfterInteraction: 0,
  }));

  const segments = {};

  filtered.forEach((d) => {
    const hour = parseInt(d.Time.split(':')[0], 10);
    const segmentId = d.Segment;

    grouped[hour].Handled += d.Handled;

    if (!segments[segmentId]) {
      segments[segmentId] = {
        hadMissedOrRejected: false,
        hadRing: false,
        hadAbandoned: false,
        hour,
      };
    }

    const validMissed = d.Missed === 1 && d.RingTime >= 20;
    if (d.Rejected === 1 || validMissed) segments[segmentId].hadMissedOrRejected = true;
    if (d.RingTime > 0) segments[segmentId].hadRing = true;
    if (d.Abandoned === 1) segments[segmentId].hadAbandoned = true;
  });

  const seen = new Set();
  Object.entries(segments).forEach(([segmentId, seg]) => {
    if (!seg.hadAbandoned || seen.has(segmentId)) return;
    const { hour } = seg;
    if (seg.hadMissedOrRejected) grouped[hour].Abandoned_AfterInteraction += 1;
    else if (seg.hadRing) grouped[hour].Abandoned_DuringRing += 1;
    else grouped[hour].Abandoned_NoInteraction += 1;
    seen.add(segmentId);
  });

  // Labels for hours
  const labels = grouped.map((g) => `${g.hour}:00`);

  // Percent handled for datalabels
  const handledPercentages = grouped.map((g) => {
    const total = g.Handled + g.Abandoned_NoInteraction + g.Abandoned_DuringRing + g.Abandoned_AfterInteraction;
    if (total === 0) return null;
    return Math.round((g.Handled / total) * 100);
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Handled',
        backgroundColor: '#4caf50',
        data: grouped.map((g) => g.Handled),
        stack: 'main',
        datalabels: {
          display: (ctx) => handledPercentages[ctx.dataIndex] !== null,
          anchor: 'end',
          align: 'start',
          formatter: (value, ctx) => `${handledPercentages[ctx.dataIndex]}%`,
          color: '#000',
          font: { weight: 'bold', size: 20 },
          offset: -30,
          clamp: false,
          clip: false,
        },
      },
      {
        label: 'Abandoned - Geen interactie',
        backgroundColor: '#e0e0e0',
        data: grouped.map((g) => g.Abandoned_NoInteraction),
        stack: 'main',
        datalabels: { display: false },
      },
      {
        label: 'Abandoned - Gedurende beltoon',
        backgroundColor: '#ffc107',
        data: grouped.map((g) => g.Abandoned_DuringRing),
        stack: 'main',
        datalabels: { display: false },
      },
      {
        label: 'Abandoned - Na gemiste/geweigerde oproep',
        backgroundColor: '#f44336',
        data: grouped.map((g) => g.Abandoned_AfterInteraction),
        stack: 'main',
        datalabels: { display: false },
      },
    ],
  };

  const maxTotal = Math.max(
    ...grouped.map(
      (g) =>
        g.Handled +
        g.Abandoned_NoInteraction +
        g.Abandoned_DuringRing +
        g.Abandoned_AfterInteraction
    )
  );

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
      },
      datalabels: {
        clip: false,
      },
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        beginAtZero: true,
        suggestedMax: maxTotal * 1.1,
      },
    },
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <div
        style={{
          backgroundColor: 'white',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'solid 0.2rem #e6f2f6',
        }}
      >
        <h3>
          Totale oproepen per uur{' '}
          {filters.selectedDate
            ? `(${filters.selectedDate})`
            : `( Laatste ${uniqueDatesCount} dagen )`}
        </h3>
        <div
          style={{
            fontSize: '0.95rem',
            color: '#555',
            padding: '0 1rem',
            marginBottom: '0.5rem',
          }}
        >
          <strong>Verwerkt:</strong> {totalHandled} &nbsp;|&nbsp;
          <strong>Abandoned ( Geen interactie ):</strong> {abandonedNoInteractionTotal} &nbsp;|&nbsp;
          <strong>Abandoned ( Gedurende beltoon ):</strong> {abandonedDuringRingTotal} &nbsp;|&nbsp;
          <strong>Abandoned ( Na gemiste / Geweigerde oproep):</strong> {abandonedAfterInteractionTotal}
        </div>
      </div>
      <ChartContainer size="medium">
        <Bar data={chartData} options={options} />
      </ChartContainer>
    </div>
  );
};

export default CallVolumePerHour;
